import { usePageMeta } from '../lib/usePageMeta.js'
import { SITE } from '../lib/site.js'
import { CASE_STUDIES } from '../lib/content.js'
import { CtaBand, IconTile, PageHero, Section, SectionHead } from '../components/ui.jsx'

const TRACKED = [
  {
    icon: 'phone',
    title: 'Calls and form leads',
    body: 'Every call is tracked, recorded and scored, so you know which channel the job came from.',
  },
  {
    icon: 'dollar',
    title: 'Cost per booked job',
    body: 'Not cost per click. We divide spend by jobs that actually landed on the calendar.',
  },
  {
    icon: 'mapPin',
    title: 'Map pack rank by city',
    body: 'Where you show up for your trade in every city you serve, checked weekly.',
  },
  {
    icon: 'trendingUp',
    title: 'Quote-to-close rate',
    body: 'How many estimates turn into signed work, and how fast your team follows up.',
  },
]

function CaseStudies() {
  return (
    <Section id="case-studies">
      <div className="flex flex-col gap-y-8">
        {CASE_STUDIES.map((c) => (
          <article
            key={c.company}
            className="grid gap-y-8 rounded-[20px] border border-line bg-white p-7 shadow-card sm:p-10 lg:grid-cols-[1fr_1.3fr] lg:gap-x-14"
          >
            <div className="flex flex-col gap-y-5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="rounded-full bg-primary-tint px-[10px] py-[4px] text-[12px] font-bold uppercase tracking-[0.08em] text-primary-dark">
                  {c.trade}
                </span>
                <span className="text-[13px] text-faint">{c.market}</span>
              </div>
              <h2 className="text-[30px] font-extrabold leading-[1.1] tracking-[-0.025em] text-ink sm:text-[36px]">
                {c.headline}
              </h2>
              <p className="text-[15px] font-semibold text-dim">{c.company}</p>
              <dl className="grid grid-cols-3 gap-x-4 border-t border-line pt-6">
                {c.results.map((r) => (
                  <div key={r.label} className="flex flex-col gap-y-1">
                    <dt className="order-2 text-[12px] leading-[1.3] text-dim">{r.label}</dt>
                    <dd className="order-1 text-[24px] font-extrabold tracking-[-0.02em] text-primary-dark">
                      {r.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="flex flex-col gap-y-3 rounded-[14px] bg-surface p-6">
                <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-faint">
                  The problem
                </span>
                <p className="text-[15px] leading-[1.6] text-ink">{c.challenge}</p>
              </div>
              <div className="flex flex-col gap-y-3 rounded-[14px] bg-night p-6 text-white">
                <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#ffb27a]">
                  What we did
                </span>
                <p className="text-[15px] leading-[1.6] text-[#dbe1ec]">{c.did}</p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </Section>
  )
}

function Tracked() {
  return (
    <Section id="what-we-track" tone="surface">
      <SectionHead
        eyebrow="Reporting"
        title="Four numbers, reported every week."
        body="If a metric does not change what we do next month, we do not put it in your report."
      />
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {TRACKED.map((t) => (
          <div
            key={t.title}
            className="flex flex-col gap-y-4 rounded-[16px] border border-line bg-white p-7 shadow-card"
          >
            <IconTile name={t.icon} />
            <h3 className="text-[18px] font-bold tracking-[-0.01em] text-ink">{t.title}</h3>
            <p className="text-[15px] leading-[1.6] text-dim">{t.body}</p>
          </div>
        ))}
      </div>
    </Section>
  )
}

export default function Results() {
  usePageMeta(
    'Results',
    'Case studies from roofing, HVAC and remodeling companies: more calls, lower cost per lead, higher quote-to-close.',
  )
  return (
    <>
      <PageHero
        eyebrow="Results"
        title="Real trades. Real markets. Real numbers."
        body="What changed for the companies we run marketing for, in the metrics an owner actually cares about."
      />
      <CaseStudies />
      <Tracked />
      <CtaBand phone={{ href: SITE.phoneHref, label: SITE.phone }} />
    </>
  )
}
